import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { admin } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ShieldCheck,
  LogOut,
  TrendingUp,
  Ticket,
  Users,
  Timer,
  IndianRupee,
  RefreshCw,
} from "lucide-react";

function fmtDuration(seconds) {
  if (!seconds) return "0m 0s";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
  }
  return `${m}m ${s}s`;
}

function fmtINR(n) {
  return "₹" + new Intl.NumberFormat("en-IN").format(n || 0);
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const s = await admin.stats();
      setStats(s);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) {
        nav("/admin");
        return;
      }
      setError("Failed to load stats");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await admin.logout();
    nav("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="mx-auto w-10 h-10 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500">
        {error || "No data"}
      </div>
    );
  }

  const totals = stats.totals || {};
  const sess = stats.sessions || {};

  return (
    <div className="min-h-screen bg-[#050505] text-white grain relative">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg,#eab308,#f59e0b)",
                boxShadow: "0 0 20px rgba(234,179,8,0.4)",
              }}
            >
              <ShieldCheck className="w-4 h-4 text-black" />
            </div>
            <div>
              <div className="font-heading font-black text-sm leading-tight">Control Room</div>
              <div className="font-mono text-[9px] tracking-widest text-zinc-500">HOUSEPARTY · ADMIN</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-900 transition"
              data-testid="admin-refresh-btn"
              title="Refresh stats"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <Button
              variant="outline"
              onClick={logout}
              className="border-zinc-800 text-zinc-400 hover:text-red-400 hover:border-red-500/50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Top KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI
            icon={<IndianRupee className="w-4 h-4" />}
            label="TOTAL REVENUE"
            value={fmtINR(totals.revenue)}
            accent="#eab308"
            testId="kpi-revenue"
          />
          <KPI
            icon={<Ticket className="w-4 h-4" />}
            label="PASSES SOLD"
            value={totals.passes_sold || 0}
            accent="#d946ef"
            testId="kpi-passes"
          />
          <KPI
            icon={<Users className="w-4 h-4" />}
            label="SESSIONS TODAY"
            value={sess.today || 0}
            sub={`${sess.distinct_djs_today || 0} unique DJs`}
            accent="#06b6d4"
            testId="kpi-sessions-today"
          />
          <KPI
            icon={<Timer className="w-4 h-4" />}
            label="AVG SESSION LENGTH"
            value={fmtDuration(sess.avg_length_seconds || 0)}
            sub={`${sess.total_sessions || 0} sessions all-time`}
            accent="#22c55e"
            testId="kpi-avg-length"
          />
        </div>

        {/* Passes breakdown */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-yellow-500" />
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">
              REVENUE BY PLAN
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.passes.map((p) => (
              <div
                key={p.plan_id}
                className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
                data-testid={`pass-row-${p.plan_id}`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-heading font-bold">{p.plan_name}</div>
                  <div
                    className="font-mono text-[10px] tracking-widest"
                    style={{
                      color: p.plan_id === "monthly_pass_499" ? "#06b6d4" : "#d946ef",
                    }}
                  >
                    {p.plan_id === "monthly_pass_499" ? "MONTHLY" : "SINGLE"}
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="font-mono text-[10px] text-zinc-500">SOLD</div>
                    <div className="font-heading font-black text-3xl">{p.count}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] text-zinc-500">REVENUE</div>
                    <div className="font-heading font-black text-3xl">{fmtINR(p.revenue)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent orders */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-widest text-zinc-500">RECENT ORDERS</span>
            <span className="font-mono text-[10px] text-zinc-600">
              LAST {stats.recent_orders?.length || 0}
            </span>
          </div>
          {(stats.recent_orders || []).length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">No orders yet.</div>
          ) : (
            <div className="divide-y divide-zinc-800/60">
              {(stats.recent_orders || []).map((o) => {
                const isPaid = o.status === "paid";
                return (
                  <div key={o.order_id} className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-900/40">
                    <div
                      className={`px-2 py-0.5 rounded-md text-[9px] font-mono font-bold tracking-widest ${
                        isPaid
                          ? "bg-green-500/10 text-green-400"
                          : o.status === "failed"
                          ? "bg-red-500/10 text-red-400"
                          : "bg-zinc-800 text-zinc-500"
                      }`}
                    >
                      {o.status?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate font-mono">{o.order_id}</div>
                      <div className="text-xs text-zinc-500 truncate">
                        {(PLAN_NAMES[o.plan_id] || o.plan_id)} · {o.user_id}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-black text-lg">₹{o.amount}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">
                        {new Date(o.created_at).toLocaleString(undefined, {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-center font-mono text-[10px] tracking-widest text-zinc-600">
          UPDATED {new Date(stats.generated_at).toLocaleTimeString()} · AUTO-REFRESH 30s
        </div>
      </main>
    </div>
  );
}

const PLAN_NAMES = {
  single_party_99: "Single Party",
  monthly_pass_499: "Monthly Pass",
};

function KPI({ icon, label, value, sub, accent, testId }) {
  return (
    <div
      className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4 relative overflow-hidden"
      data-testid={testId}
    >
      <div
        className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-20"
        style={{ background: accent, filter: "blur(20px)" }}
      />
      <div className="flex items-center gap-2 text-zinc-500 mb-2 relative">
        <span style={{ color: accent }}>{icon}</span>
        <span className="font-mono text-[9px] tracking-widest">{label}</span>
      </div>
      <div className="font-heading font-black text-3xl leading-none relative">{value}</div>
      {sub && (
        <div className="text-xs text-zinc-500 mt-1 relative" data-testid={`${testId}-sub`}>
          {sub}
        </div>
      )}
    </div>
  );
}
